
import React, { useState } from 'react';
import { Patient } from '../../types';
import { useData } from '../../context/DataContext';
import { Edit, Trash2, Eye, AlertTriangle } from 'lucide-react';
import { calculateAge } from '../../utils/calculations';
import Modal from '../shared/Modal';
import Button from '../shared/Button';

interface PatientListProps {
  patients: Patient[];
  onEdit: (patient: Patient) => void;
  onViewDetails: (patient: Patient) => void;
  emptyMessage?: string;
}

const PatientList: React.FC<PatientListProps> = ({ patients, onEdit, onViewDetails, emptyMessage }) => {
  const { deletePatient } = useData();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState<{id: number, name: string} | null>(null);

  const handleDeleteClick = (id: number, name: string) => {
    setPatientToDelete({ id, name });
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (patientToDelete) {
      deletePatient(patientToDelete.id);
      setIsDeleteModalOpen(false);
      setPatientToDelete(null);
    }
  };

  if (patients.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow border-2 border-dashed border-slate-200">
        <h3 className="text-sm font-medium text-slate-500">
          {emptyMessage || "Nenhum paciente encontrado."}
        </h3>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden border border-slate-200">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Nome</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Contato</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Idade</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Gênero</th>
              <th scope="col" className="relative px-6 py-3"><span className="sr-only">Ações</span></th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {patients.map((patient) => {
              const age = calculateAge(patient.birthDate);
              return (
                <tr key={patient.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-slate-900">{patient.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-slate-500">{patient.phone}</div>
                    <div className="text-sm text-slate-500">{patient.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                    {age > 0 ? `${age} anos` : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{patient.gender}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end items-center gap-2">
                      <button onClick={() => onViewDetails(patient)} className="text-indigo-600 hover:text-indigo-900 p-1 rounded-full hover:bg-indigo-100 transition-colors" title="Ver Detalhes">
                        <Eye size={18} />
                      </button>
                      <button onClick={() => onEdit(patient)} className="text-yellow-600 hover:text-yellow-900 p-1 rounded-full hover:bg-yellow-100 transition-colors" title="Editar">
                        <Edit size={18} />
                      </button>
                      <button onClick={() => handleDeleteClick(patient.id, patient.name)} className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-100 transition-colors" title="Excluir">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO DE PACIENTE */}
      <Modal 
        isOpen={isDeleteModalOpen} 
        onClose={() => setIsDeleteModalOpen(false)} 
        title="Confirmar Exclusão de Paciente"
        size="md"
      >
        <div className="space-y-6 text-center">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center text-red-600 mb-4">
            <AlertTriangle size={32} />
          </div>
          <div>
            <h4 className="text-lg font-bold text-slate-800">Atenção Crítica</h4>
            <p className="text-sm text-slate-500 mt-2">
              Deseja realmente excluir o paciente <span className="font-bold text-slate-700">"{patientToDelete?.name}"</span>?<br/><br/>
              <span className="text-red-600 font-bold uppercase text-xs">Aviso:</span> Todos os agendamentos, planos e avaliações vinculados serão apagados permanentemente.
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="ghost" 
              className="flex-1" 
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button 
              variant="danger" 
              className="flex-1" 
              onClick={confirmDelete}
            >
              Excluir Tudo
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PatientList;
